(() => {
  const sliders = [];

  function parseNumber(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getPerView(slider) {
    const width = window.innerWidth;
    const mobileBp = parseInt(slider.dataset.breakpointMobile || '540', 10);
    const tabletBp = parseInt(slider.dataset.breakpointTablet || '850', 10);
    if (slider.dataset.slidesPerViewMobile && width <= mobileBp) {
      return parseNumber(slider.dataset.slidesPerViewMobile, 1);
    }
    if (slider.dataset.slidesPerViewTablet && width <= tabletBp) {
      return parseNumber(slider.dataset.slidesPerViewTablet, 1);
    }
    return parseNumber(slider.dataset.slidesPerView, 1);
  }

  function getSlides(track) {
    return Array.from(track.children).filter((child) => !child.hasAttribute('data-vanilla-slider-ignore'));
  }

  function setActiveSlide(slider, track, slides, index) {
    slides.forEach((slide, idx) => {
      slide.classList.toggle('is-active', idx === index);
    });
    slider.dataset.activeIndex = index;
  }

  function updateDots(slider, dotsContainer, pageCount, pageIndex) {
    if (!dotsContainer) return;
    const dotItems = dotsContainer.querySelectorAll('li');
    dotItems.forEach((dotItem, idx) => {
      dotItem.classList.toggle('slick-active', idx === pageIndex);
    });
  }

  function getPageIndex(track, perView) {
    if (perView <= 1) {
      const slideIndex = Math.round(track.scrollLeft / track.clientWidth);
      return Math.max(0, slideIndex);
    }
    const pageWidth = track.clientWidth;
    return Math.max(0, Math.round(track.scrollLeft / pageWidth));
  }

  function scrollToPage(track, pageIndex) {
    const pageWidth = track.clientWidth;
    track.scrollTo({ left: pageIndex * pageWidth, behavior: 'smooth' });
  }

  function findDotsContainer(slider) {
    const local = slider.querySelector('[data-vanilla-slider-dots]');
    if (local) return local;
    const parent = slider.parentElement;
    if (!parent) return null;
    return Array.from(parent.children).find((child) => child.hasAttribute('data-vanilla-slider-dots')) || null;
  }

  function buildDots(slider, track, slides, perView) {
    const dotsContainer = findDotsContainer(slider);
    if (!dotsContainer) return;
    dotsContainer.innerHTML = '';
    const perViewFloor = Math.max(1, Math.floor(perView));
    const pageCount = Math.max(1, Math.ceil(slides.length / perViewFloor));
    for (let i = 0; i < pageCount; i += 1) {
      const dotItem = document.createElement('li');
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'vanilla-slider__dot';
      dot.addEventListener('click', () => scrollToPage(track, i));
      dotItem.appendChild(dot);
      dotsContainer.appendChild(dotItem);
    }
    updateDots(slider, dotsContainer, pageCount, 0);
  }

  function updateProgress(slider, track, slides, perView) {
    const selector = slider.dataset.progressSelector;
    if (!selector) return;
    const bar = slider.querySelector(selector) || slider.parentElement?.querySelector(selector);
    if (!bar) return;
    const perViewFloor = Math.max(1, Math.floor(perView));
    const pageCount = Math.max(1, Math.ceil(slides.length / perViewFloor));
    const pageIndex = getPageIndex(track, perView);
    const progress = Math.min(((pageIndex + 1) / pageCount) * 100, 100);
    bar.style.width = `${progress}%`;
  }

  function initSlider(slider) {
    const track = slider.querySelector('[data-vanilla-slider-track]') || slider;
    const scope = slider.parentElement || slider;
    const prev = slider.querySelector('[data-vanilla-slider-prev]') || scope.querySelector('[data-vanilla-slider-prev]');
    const next = slider.querySelector('[data-vanilla-slider-next]') || scope.querySelector('[data-vanilla-slider-next]');
    const slides = getSlides(track);
    const isFade = slider.hasAttribute('data-vanilla-slider-fade');
    const isLoop = slider.dataset.loop === 'true';
    const autoplay = slider.dataset.autoplay === 'true';
    const autoplayInterval = parseInt(slider.dataset.autoplayInterval || '3000', 10);
    let perView = getPerView(slider);

    slider.classList.add('slick-slider', 'slick-initialized');
    track.classList.add('slick-track');
    slides.forEach((slide) => slide.classList.add('slick-slide'));

    track.style.setProperty('--slides-per-view', perView);

    if (isFade) {
      setActiveSlide(slider, track, slides, 0);
    }

    buildDots(slider, track, slides, perView);
    updateProgress(slider, track, slides, perView);

    function handleResize() {
      perView = getPerView(slider);
      track.style.setProperty('--slides-per-view', perView);
      buildDots(slider, track, slides, perView);
      updateProgress(slider, track, slides, perView);
    }

    function handleScroll() {
      if (isFade) return;
      const pageIndex = getPageIndex(track, perView);
      updateDots(slider, findDotsContainer(slider), 0, pageIndex);
      updateProgress(slider, track, slides, perView);
    }

    if (prev) {
      prev.classList.add('slick-prev');
      prev.addEventListener('click', () => {
        if (isFade) {
          const current = parseInt(slider.dataset.activeIndex || '0', 10);
          const nextIndex = (current - 1 + slides.length) % slides.length;
          setActiveSlide(slider, track, slides, nextIndex);
          return;
        }
        const pageIndex = getPageIndex(track, perView);
        if (pageIndex <= 0 && isLoop) {
          const perViewFloor = Math.max(1, Math.floor(perView));
          const pageCount = Math.max(1, Math.ceil(slides.length / perViewFloor));
          scrollToPage(track, pageCount - 1);
          return;
        }
        scrollToPage(track, Math.max(0, pageIndex - 1));
      });
    }

    if (next) {
      next.classList.add('slick-next');
      next.addEventListener('click', () => {
        if (isFade) {
          const current = parseInt(slider.dataset.activeIndex || '0', 10);
          const nextIndex = (current + 1) % slides.length;
          setActiveSlide(slider, track, slides, nextIndex);
          return;
        }
        const pageIndex = getPageIndex(track, perView);
        const perViewFloor = Math.max(1, Math.floor(perView));
        const pageCount = Math.max(1, Math.ceil(slides.length / perViewFloor));
        if (pageIndex >= pageCount - 1 && isLoop) {
          scrollToPage(track, 0);
          return;
        }
        scrollToPage(track, pageIndex + 1);
      });
    }

    if (!isFade) {
      track.addEventListener('scroll', () => requestAnimationFrame(handleScroll));
    }

    window.addEventListener('resize', handleResize);

    if (autoplay) {
      setInterval(() => {
        if (isFade) {
          const current = parseInt(slider.dataset.activeIndex || '0', 10);
          const nextIndex = (current + 1) % slides.length;
          setActiveSlide(slider, track, slides, nextIndex);
          return;
        }
        const pageIndex = getPageIndex(track, perView);
        const perViewFloor = Math.max(1, Math.floor(perView));
        const pageCount = Math.max(1, Math.ceil(slides.length / perViewFloor));
        if (pageIndex >= pageCount - 1 && isLoop) {
          scrollToPage(track, 0);
          return;
        }
        scrollToPage(track, pageIndex + 1);
      }, autoplayInterval);
    }
  }

  function initAll() {
    sliders.length = 0;
    document.querySelectorAll('[data-vanilla-slider]').forEach((slider) => {
      if (slider.dataset.vanillaSliderInit === 'true') return;
      slider.dataset.vanillaSliderInit = 'true';
      initSlider(slider);
      sliders.push(slider);
    });
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', initAll);
  }
})();
